import { z } from 'zod';

export const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Femenino' },
  { value: 'OTHER', label: 'Otro' },
] as const;

export const COMPENSATION_PLAN_OPTIONS = [
  { value: 'A', label: 'Plan A' },
  { value: 'B', label: 'Plan B' },
] as const;

export const SAVINGS_MODE_OPTIONS = [
  { value: 'goal_value', label: 'Objetivo de ahorro' },
  { value: 'savings_capacity', label: 'Capacidad de ahorro' },
  { value: 'withdrawal_goal', label: 'Retiros periódicos' },
] as const;

export const PAYMENT_PERIODICITY_OPTIONS = [
  { value: 'MONTHLY', label: 'Mensual' },
  { value: 'QUARTERLY', label: 'Trimestral' },
  { value: 'SEMIANNUAL', label: 'Semestral' },
  { value: 'ANNUAL', label: 'Anual' },
] as const;

const sharedFields = {
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']),
  projection_rate: z.coerce.number().positive('Debe ser > 0'),
  compensation_plan: z.enum(['A', 'B']),
  payment_periodicity: z.enum(['MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL']),
  unscheduled_annual_contribution: z.coerce.number().nonnegative(),
  death_insured_capital: z.coerce.number().min(300, 'Mínimo 300 UF').max(20000, 'Máximo 20.000 UF'),
  accidental_death_insured_capital: z.coerce.number().nonnegative(),
  itp_insured_capital: z.coerce.number().nonnegative(),
  serious_illness_insured_capital: z.coerce.number().nonnegative(),
};

export const familySavingsInsuranceSchema = z.discriminatedUnion('savings_mode', [
  z.object({
    ...sharedFields,
    savings_mode: z.literal('goal_value'),
    goal: z.coerce.number().nonnegative('Debe ser ≥ 0'),
    goal_value_withdrawal_month: z.coerce.number().int().positive('Debe ser > 0'),
  }),
  z.object({
    ...sharedFields,
    savings_mode: z.literal('savings_capacity'),
    agreed_premium: z.coerce.number().min(2, 'Mínimo 2 UF'),
  }),
  z.object({
    ...sharedFields,
    savings_mode: z.literal('withdrawal_goal'),
    withdrawal_amount: z.coerce.number().nonnegative('Debe ser ≥ 0'),
    withdrawal_period_months: z.coerce.number().int().nonnegative('Debe ser ≥ 0'),
  }),
]);

export type FamilySavingsInsuranceFormValues = z.infer<typeof familySavingsInsuranceSchema>;

const sharedDefaults = {
  date_of_birth: '1990-01-01' as const,
  gender: 'MALE' as const,
  projection_rate: 0.02,
  compensation_plan: 'A' as const,
  payment_periodicity: 'MONTHLY' as const,
  unscheduled_annual_contribution: 0,
  death_insured_capital: 300,
  accidental_death_insured_capital: 0,
  itp_insured_capital: 0,
  serious_illness_insured_capital: 0,
};

export const familySavingsInsuranceDefaults: FamilySavingsInsuranceFormValues = {
  ...sharedDefaults,
  savings_mode: 'goal_value',
  goal: 0,
  goal_value_withdrawal_month: 120,
};
